/**
 * Plays the synthesised sound design, with separate music and sfx channels.
 *
 * Phaser-free: this talks to the Web Audio API directly, because everything it
 * plays is generated rather than loaded. Scenes reach it through the registry.
 *
 * Three things browsers force on any audio code, all handled here:
 *
 * * An AudioContext starts suspended until a real user gesture, so the context
 *   is created lazily and `resume()` is retried on every call.
 * * Audio is optional. Every entry point is wrapped so a blocked or missing
 *   AudioContext silently does nothing rather than breaking the game.
 * * Scheduling has to run ahead of the clock. The music scheduler queues a
 *   couple of seconds at a time, which is what stops notes jittering when the
 *   main thread is busy drawing.
 */

import {
  musicBedFor,
  nextDegree,
  SFX,
  noteHz,
  scaleNote,
  type MusicBed,
  type SfxName,
  type Tone,
} from './soundDesign';
import type { BackgroundTheme } from '../types';

/** Master trim, so one sound's gain can be read as a fraction of the mix. */
const SFX_VOLUME = 0.9;
const MUSIC_VOLUME = 0.55;

/** How far ahead notes are queued, and how often the scheduler wakes. */
const LOOKAHEAD_S = 2.0;
const SCHEDULE_MS = 700;

/** A keystroke can outrun any sound; below this gap the click is dropped. */
const KEY_MIN_GAP_S = 0.035;

export interface AudioSettings {
  readonly musicMuted: boolean;
  readonly sfxMuted: boolean;
}

type ContextFactory = () => AudioContext | null;

function defaultFactory(): AudioContext | null {
  const win = globalThis as unknown as {
    AudioContext?: new (options?: AudioContextOptions) => AudioContext;
    webkitAudioContext?: new (options?: AudioContextOptions) => AudioContext;
  };
  const Ctor = win.AudioContext ?? win.webkitAudioContext;
  if (Ctor === undefined) return null;
  try {
    // The click fires on a keystroke, so latency is the thing that matters
    // most here: a default context measured 40ms of output latency, which is
    // enough to hear the sound trail the key.
    return new Ctor({ latencyHint: 'interactive' });
  } catch {
    try {
      return new Ctor();
    } catch {
      return null;
    }
  }
}

export class AudioManager {
  private context: AudioContext | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private noise: AudioBuffer | null = null;

  private settings: AudioSettings = { musicMuted: false, sfxMuted: false };
  private bed: MusicBed | null = null;
  private theme: BackgroundTheme | null = null;
  private timer: ReturnType<typeof setInterval> | undefined;
  private nextNoteAt = 0;
  private degree = 0;
  /** Music speed: 1 is the bed as written; Tide Run raises it as the water nears. */
  private tempo = 1;
  private lastKeyAt = -1;

  constructor(private readonly createContext: ContextFactory = defaultFactory) {}

  /* --------------------------------------------------------------- state */

  applySettings(settings: AudioSettings): void {
    this.settings = settings;
    if (this.musicBus !== null && this.context !== null) {
      this.musicBus.gain.setTargetAtTime(
        settings.musicMuted ? 0 : MUSIC_VOLUME,
        this.context.currentTime,
        0.05,
      );
    }
    if (this.sfxBus !== null) {
      this.sfxBus.gain.value = settings.sfxMuted ? 0 : SFX_VOLUME;
    }
    // Stop the scheduler outright when muted: there is no point burning a
    // timer every 700ms to queue notes at zero gain.
    if (settings.musicMuted) this.stopMusic();
    else if (this.theme !== null && this.timer === undefined) this.startMusic(this.theme);
  }

  /** Call from a real input handler; browsers ignore it otherwise. */
  unlock(): void {
    const context = this.ensureContext();
    if (context !== null && context.state === 'suspended') void context.resume();
  }

  /* ----------------------------------------------------------------- sfx */

  play(name: SfxName): void {
    if (this.settings.sfxMuted) return;
    const context = this.ensureContext();
    if (context === null || this.sfxBus === null) return;
    if (context.state === 'suspended') void context.resume();

    const now = context.currentTime;
    if (name === 'key') {
      if (now - this.lastKeyAt < KEY_MIN_GAP_S) return;
      this.lastKeyAt = now;
    }
    for (const tone of SFX[name]) this.renderTone(context, this.sfxBus, tone, now);
  }

  /* --------------------------------------------------------------- music */

  startMusic(theme: BackgroundTheme): void {
    this.theme = theme;
    if (this.settings.musicMuted) return;

    const context = this.ensureContext();
    if (context === null) return;
    if (context.state === 'suspended') void context.resume();

    const bed = musicBedFor(theme);
    const changed = this.bed === null || this.bed.root !== bed.root || this.bed.beat !== bed.beat;
    this.bed = bed;
    if (changed) this.degree = 0;
    this.nextNoteAt = Math.max(this.nextNoteAt, context.currentTime + 0.25);

    if (this.timer !== undefined) return;
    this.schedule();
    this.timer = setInterval(() => this.schedule(), SCHEDULE_MS);
  }

  /**
   * Speed the music up (Tide Run, as the water nears) or back to normal at 1.
   * Takes effect from the next note, so it never stutters.
   */
  setTempo(factor: number): void {
    this.tempo = Math.min(2, Math.max(0.5, factor));
  }

  stopMusic(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
  }

  /** Releases the audio device; call when the game is torn down. */
  destroy(): void {
    this.stopMusic();
    const context = this.context;
    this.context = null;
    this.sfxBus = null;
    this.musicBus = null;
    if (context !== null) void context.close().catch(() => undefined);
  }

  /**
   * Queue every note that falls inside the lookahead window.
   *
   * The melody is a random walk over the pentatonic scale, which cannot land
   * on a dissonant interval, with a quiet root drone under every fourth note
   * to keep it anchored.
   */
  private schedule(): void {
    const context = this.context;
    const bed = this.bed;
    if (context === null || bed === null || this.musicBus === null) return;

    const until = context.currentTime + LOOKAHEAD_S;
    let guard = 0;
    while (this.nextNoteAt < until && guard < 64) {
      guard += 1;
      const at = Math.max(this.nextNoteAt, context.currentTime);

      this.renderTone(
        context,
        this.musicBus,
        {
          wave: bed.wave,
          from: noteHz(scaleNote(bed, this.degree)),
          duration: bed.beat * 1.6,
          gain: bed.gain,
          attack: bed.beat * 0.35,
        },
        at,
      );

      if (this.degree % 4 === 0) {
        this.renderTone(
          context,
          this.musicBus,
          {
            wave: 'sine',
            from: noteHz(scaleNote(bed, 0) - 12),
            duration: bed.beat * 3.2,
            gain: bed.gain * 0.5,
            attack: bed.beat * 0.6,
          },
          at,
        );
      }

      this.degree = nextDegree(this.degree, bed.range, Math.random);
      this.nextNoteAt = at + bed.beat / this.tempo;
    }
  }

  /* -------------------------------------------------------------- render */

  private renderTone(context: AudioContext, bus: GainNode, tone: Tone, now: number): void {
    const start = now + (tone.delay ?? 0);
    const end = start + tone.duration;
    const attack = Math.min(tone.attack ?? 0.008, tone.duration * 0.5);

    const peak = Math.max(tone.gain, 0.0002);
    const gain = context.createGain();
    if (attack <= 0) {
      // A click is all transient. Ramping up to it, even over 2ms, softens the
      // onset and reads as the sound arriving after the key.
      gain.gain.setValueAtTime(peak, start);
    } else {
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(peak, start + attack);
    }
    // Exponential to a floor rather than to zero: a true zero is invalid for
    // an exponential ramp, and a linear tail clicks on these short sounds.
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    gain.connect(bus);

    if (tone.noise === true) {
      const source = context.createBufferSource();
      source.buffer = this.ensureNoise(context);
      source.loop = true;
      const filter = context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = tone.from;
      source.connect(filter).connect(gain);
      source.start(start);
      source.stop(end + 0.02);
      return;
    }

    const osc = context.createOscillator();
    osc.type = tone.wave ?? 'sine';
    osc.frequency.setValueAtTime(tone.from, start);
    if (tone.to !== undefined && tone.to !== tone.from) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(tone.to, 1), end);
    }
    osc.connect(gain);
    osc.start(start);
    osc.stop(end + 0.02);
  }

  private ensureNoise(context: AudioContext): AudioBuffer {
    if (this.noise !== null) return this.noise;
    const buffer = context.createBuffer(
      1,
      Math.floor(context.sampleRate * 0.5),
      context.sampleRate,
    );
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    this.noise = buffer;
    return buffer;
  }

  private ensureContext(): AudioContext | null {
    if (this.context !== null) return this.context;

    const context = this.createContext();
    if (context === null) return null;

    this.context = context;
    this.sfxBus = context.createGain();
    this.sfxBus.gain.value = this.settings.sfxMuted ? 0 : SFX_VOLUME;
    this.sfxBus.connect(context.destination);

    this.musicBus = context.createGain();
    this.musicBus.gain.value = this.settings.musicMuted ? 0 : MUSIC_VOLUME;
    this.musicBus.connect(context.destination);
    return context;
  }
}
